import React, { FC, useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Modal, Platform, ScrollView, TouchableOpacity, View } from 'react-native'
import { AppBackground, ResponsiveLogo } from '@ui/components'

import { BodyText, Headline2, Headline5, colors } from '@/aena/styles'
import { HeaderWithBackAena } from '@/components/headerwithback'

import {
  getTravelExportDestinations,
  getTravelImportOrigins,
  getTravelImportScenarios,
  getTravelDocSources,
  getTravelPev,
  submitTravelDocContactRequest,
  buildExportFuentes,
  buildImportFuentes,
  ITravelExportDestination,
  ITravelImportOrigin,
  ITravelImportScenario,
  ITravelDocSource,
  ITravelPev,
  TravelDocEscenario,
  TravelDocPetType,
} from '@front/apis'

type Tab = 'export' | 'import'

const verde = colors.aenaVerde
const grisBorde = '#E5E5E5'
const grisSuave = '#FAFAFA'
const okBg = '#ECFDF5'
const okFg = '#10B981'
const warnBg = '#FFFBEB'
const warnFg = '#B45309'
const blockBg = '#FEF2F2'
const blockFg = '#B91C1C'
const infoBg = '#EFF6FF'
const infoFg = '#1E40AF'

export const AenaTravelDocs: FC = () => {
  const [tab, setTab] = useState<Tab>('export')
  const [searchExport, setSearchExport] = useState('')
  const [searchImport, setSearchImport] = useState('')

  const [destinations, setDestinations] = useState<ITravelExportDestination[]>([])
  const [origins, setOrigins] = useState<ITravelImportOrigin[]>([])
  const [scenarios, setScenarios] = useState<Record<TravelDocEscenario, ITravelImportScenario> | null>(null)
  const [sources, setSources] = useState<Record<string, ITravelDocSource>>({})
  const [pev, setPev] = useState<ITravelPev>({ aeropuertos: [], puertos: [], fronteras: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedExport, setSelectedExport] = useState<ITravelExportDestination | null>(null)
  const [selectedImport, setSelectedImport] = useState<ITravelImportOrigin | null>(null)
  const [contactFor, setContactFor] = useState<{ pais: string; iso: string; vista: 'export' | 'import' } | null>(null)

  const openContact = (info: { pais: string; iso: string; vista: 'export' | 'import' }) => {
    setSelectedExport(null)
    setSelectedImport(null)
    setContactFor(info)
  }

  // Bloquea el scroll del body mientras haya un modal abierto (solo web).
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const anyOpen = selectedExport !== null || selectedImport !== null || contactFor !== null
    const prev = document.body.style.overflow
    document.body.style.overflow = anyOpen ? 'hidden' : prev || ''
    return () => { document.body.style.overflow = prev || '' }
  }, [selectedExport, selectedImport, contactFor])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const [d, o, s, src, p] = await Promise.all([
          getTravelExportDestinations(),
          getTravelImportOrigins(),
          getTravelImportScenarios(),
          getTravelDocSources(),
          getTravelPev(),
        ])
        if (cancelled) return
        setDestinations(d)
        setOrigins(o)
        setScenarios(s)
        setSources(src)
        setPev(p)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Error cargando documentación')
        console.error('travel-docs load error', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const exportFiltered = useMemo(() => {
    const q = searchExport.trim().toLowerCase()
    if (!q) return destinations
    return destinations.filter(
      (d) => d.pais.toLowerCase().includes(q) || d.iso.toLowerCase().includes(q)
    )
  }, [searchExport, destinations])

  const importFiltered = useMemo(() => {
    const q = searchImport.trim().toLowerCase()
    if (!q) return origins
    return origins.filter(
      (d) => d.pais.toLowerCase().includes(q) || d.iso.toLowerCase().includes(q)
    )
  }, [searchImport, origins])

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title="Documentación viajes exterior" navigateTo="/aena/home" />

        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: grisBorde, paddingHorizontal: 16 }}>
          <TabButton active={tab === 'export'} label="Destinos desde España" count={destinations.length} onPress={() => setTab('export')} />
          <TabButton active={tab === 'import'} label="Orígenes hacia España" count={origins.length} onPress={() => setTab('import')} />
        </View>

        <ScrollView style={{ padding: 16 }} keyboardShouldPersistTaps="handled">
          {loading ? (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <ActivityIndicator size="large" color={verde} />
              <BodyText style={{ marginTop: 12, color: colors.grisOscuro }}>Cargando documentación…</BodyText>
            </View>
          ) : error ? (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <BodyText style={{ color: blockFg }}>{error}</BodyText>
            </View>
          ) : tab === 'export' ? (
            <CountriesView
              placeholder="Buscar país o ISO (ej: Alemania, DE)"
              value={searchExport}
              onChange={setSearchExport}
              items={exportFiltered}
              renderCard={(d) => (
                <ExportCard key={`${d.iso}-${d.pais}`} d={d} onPress={() => setSelectedExport(d)} />
              )}
            />
          ) : (
            <CountriesView
              placeholder="Buscar país o ISO (ej: Brasil, BR)"
              value={searchImport}
              onChange={setSearchImport}
              items={importFiltered}
              renderCard={(d) => (
                <ImportCard key={`${d.iso}-${d.pais}`} d={d} onPress={() => setSelectedImport(d)} />
              )}
            />
          )}
        </ScrollView>
      </View>

      <ExportModal d={selectedExport} sources={sources} onClose={() => setSelectedExport(null)} onContact={openContact} />
      <ImportModal d={selectedImport} scenarios={scenarios} sources={sources} pev={pev} onClose={() => setSelectedImport(null)} onContact={openContact} />
      <ContactFormModal info={contactFor} onClose={() => setContactFor(null)} />
    </AppBackground>
  )
}

export default AenaTravelDocs

const TabButton: FC<{ active: boolean; label: string; count: number; onPress: () => void }> = ({ active, label, count, onPress }) => (
  <TouchableOpacity
    onPress={onPress}
    style={{
      paddingVertical: 14,
      paddingHorizontal: 12,
      borderBottomWidth: 3,
      borderBottomColor: active ? verde : 'transparent',
      marginRight: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    }}
  >
    <BodyText style={{ fontWeight: '600', color: active ? colors.azulCorporativo : colors.grisOscuro, fontSize: 14 }}>{label}</BodyText>
    <View style={{ backgroundColor: active ? verde : grisSuave, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 12 }}>
      <BodyText style={{ fontSize: 11, fontWeight: '700', color: active ? colors.blanco : colors.grisOscuro }}>{count}</BodyText>
    </View>
  </TouchableOpacity>
)

interface CountriesViewProps<T> {
  placeholder: string
  value: string
  onChange: (v: string) => void
  items: T[]
  renderCard: (item: T) => React.ReactNode
}

function CountriesView<T>({ placeholder, value, onChange, items, renderCard }: CountriesViewProps<T>) {
  return (
    <View>
      <SearchInput placeholder={placeholder} value={value} onChange={onChange} />
      {items.length === 0 ? (
        <View style={{ alignItems: 'center', paddingVertical: 60 }}>
          <BodyText style={{ color: colors.grisOscuro }}>Ningún país coincide con la búsqueda.</BodyText>
        </View>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
          {items.map(renderCard)}
        </View>
      )}
    </View>
  )
}

const SearchInput: FC<{ placeholder: string; value: string; onChange: (v: string) => void }> = ({ placeholder, value, onChange }) => {
  if (Platform.OS === 'web') {
    return (
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 14px',
          fontSize: 15,
          fontFamily: 'inherit',
          border: `1.5px solid ${grisBorde}`,
          borderRadius: 8,
          outline: 'none',
          background: colors.blanco,
          color: colors.negro,
          boxSizing: 'border-box',
        } as any}
      />
    )
  }
  return null
}

const CardShell: FC<{ onPress: () => void; children: React.ReactNode }> = ({ onPress, children }) => (
  <TouchableOpacity
    onPress={onPress}
    activeOpacity={0.85}
    style={{
      backgroundColor: colors.blanco,
      borderRadius: 12,
      padding: 14,
      borderWidth: 1,
      borderColor: grisBorde,
      minWidth: 240,
      flexGrow: 1,
      flexBasis: 240,
      maxWidth: 360,
    }}
  >
    {children}
  </TouchableOpacity>
)

const ExportCard: FC<{ d: ITravelExportDestination; onPress: () => void }> = ({ d, onPress }) => (
  <CardShell onPress={onPress}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
      <View style={{ backgroundColor: grisSuave, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
        <BodyText style={{ fontSize: 11, fontWeight: '700', color: verde }}>{d.iso}</BodyText>
      </View>
      <Tag {...tagComplejidad(d.complejidad)} />
    </View>
    <Headline5 style={{ fontSize: 16, marginBottom: 2 }}>{d.pais}</Headline5>
    <BodyText style={{ fontSize: 11, color: colors.grisOscuro, textTransform: 'uppercase', fontWeight: '600', marginBottom: 10 }}>
      {d.continente} · {d.cluster}
    </BodyText>
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {d.soloCargo && <Tag bg={colors.negro} fg={colors.blanco} label="Solo cargo" />}
      {d.razasTipo === 'block' && <Tag bg={blockBg} fg={blockFg} label="Razas prohibidas" />}
      {d.razasTipo === 'warn' && <Tag bg={warnBg} fg={warnFg} label="Razas con licencia" />}
    </View>
  </CardShell>
)

const ImportCard: FC<{ d: ITravelImportOrigin; onPress: () => void }> = ({ d, onPress }) => (
  <CardShell onPress={onPress}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
      <View style={{ backgroundColor: grisSuave, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
        <BodyText style={{ fontSize: 11, fontWeight: '700', color: verde }}>{d.iso}</BodyText>
      </View>
      <Tag {...tagComplejidad(d.complejidad)} />
    </View>
    <Headline5 style={{ fontSize: 16, marginBottom: 2 }}>{d.pais}</Headline5>
    <BodyText style={{ fontSize: 11, color: colors.grisOscuro, textTransform: 'uppercase', fontWeight: '600', marginBottom: 10 }}>
      {d.continente}
    </BodyText>
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {d.asimiladoUE && <Tag bg={infoBg} fg={infoFg} label="Asimilado UE" />}
      {d.notaEspecial && <Tag bg={blockBg} fg={blockFg} label="Nota crítica" />}
    </View>
  </CardShell>
)

const Tag: FC<{ bg: string; fg: string; label: string }> = ({ bg, fg, label }) => (
  <View style={{ backgroundColor: bg, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 5 }}>
    <BodyText style={{ fontSize: 11, fontWeight: '600', color: fg }}>{label}</BodyText>
  </View>
)

function tagComplejidad(c: 'BAJA' | 'MEDIA' | 'ALTA') {
  if (c === 'BAJA') return { bg: okBg, fg: okFg, label: 'Doc. BAJA' }
  if (c === 'MEDIA') return { bg: warnBg, fg: warnFg, label: 'Doc. MEDIA' }
  return { bg: blockBg, fg: blockFg, label: 'Doc. ALTA' }
}

// ============================== MODALES ==============================

const ModalShell: FC<{ visible: boolean; onClose: () => void; title: string; subtitle: string; children: React.ReactNode }> = ({ visible, onClose, title, subtitle, children }) => (
  <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
    <View style={{ flex: 1, backgroundColor: colors.blanco }}>
      <View style={{ backgroundColor: verde, padding: 20, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <View style={{ flex: 1 }}>
          <Headline2 style={{ color: colors.blanco, fontSize: 24, marginBottom: 4 }}>{title}</Headline2>
          <BodyText style={{ color: colors.blanco, opacity: 0.9, fontSize: 14 }}>{subtitle}</BodyText>
        </View>
        <TouchableOpacity
          onPress={onClose}
          style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.25)', alignItems: 'center', justifyContent: 'center' }}
        >
          <BodyText style={{ color: colors.blanco, fontSize: 20, lineHeight: 20 }}>×</BodyText>
        </TouchableOpacity>
      </View>
      <ScrollView style={{ flex: 1, padding: 20 }} contentContainerStyle={{ paddingBottom: 40 }}>
        {children}
      </ScrollView>
    </View>
  </Modal>
)

const Section: FC<{ title: string; children: React.ReactNode; titleFontSize?: number }> = ({ title, children, titleFontSize = 17 }) => (
  <View style={{ marginBottom: 20 }}>
    <View style={{ paddingBottom: 6, borderBottomWidth: 2, borderBottomColor: grisBorde, marginBottom: 10 }}>
      <Headline5 style={{ fontSize: titleFontSize, fontWeight: '700' }}>{title}</Headline5>
    </View>
    {children}
  </View>
)

const Row: FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <View style={{ flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: grisBorde, flexWrap: 'wrap' }}>
    <View style={{ width: 180, minWidth: 140 }}>
      <BodyText style={{ fontSize: 14, fontWeight: '600', color: colors.grisOscuro }}>{label}</BodyText>
    </View>
    <View style={{ flex: 1, minWidth: 200 }}>
      {typeof value === 'string' ? (
        <BodyText style={{ fontSize: 15, color: colors.negro }}>{value}</BodyText>
      ) : (
        value
      )}
    </View>
  </View>
)

const Alert: FC<{ kind: 'info' | 'warn' | 'block' | 'ok'; children: React.ReactNode }> = ({ kind, children }) => {
  const cfg = {
    info: { bg: infoBg, fg: infoFg },
    warn: { bg: warnBg, fg: warnFg },
    block: { bg: blockBg, fg: blockFg },
    ok: { bg: okBg, fg: okFg },
  }[kind]
  return (
    <View style={{ backgroundColor: cfg.bg, borderLeftWidth: 4, borderLeftColor: cfg.fg, padding: 12, borderRadius: 6, marginBottom: 12 }}>
      <BodyText style={{ fontSize: 15, color: cfg.fg, lineHeight: 22 }}>{children}</BodyText>
    </View>
  )
}

const FuentesList: FC<{ fuentes: ITravelDocSource[] }> = ({ fuentes }) => (
  <Section title="Fuentes legales consultadas" titleFontSize={14}>
    <View style={{ gap: 8 }}>
      {fuentes.map((f, i) => (
        <View key={i} style={{ padding: 10, backgroundColor: grisSuave, borderLeftWidth: 3, borderLeftColor: verde, borderRadius: 6 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
            <BodyText style={{ fontWeight: '700', fontSize: 13, color: colors.negro, flex: 1 }}>{f.titulo}</BodyText>
            <View style={{ backgroundColor: verde, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 4 }}>
              <BodyText style={{ fontSize: 10, fontWeight: '700', color: colors.blanco }}>{f.tipo}</BodyText>
            </View>
          </View>
          <BodyText style={{ fontSize: 12, color: colors.grisOscuro, lineHeight: 17, marginBottom: 4 }}>{f.desc}</BodyText>
          {f.url ? (
            <BodyText
              style={{ fontSize: 11, color: verde, fontWeight: '600' }}
              onPress={() => Platform.OS === 'web' && window.open(f.url as string, '_blank')}
            >
              {f.url}
            </BodyText>
          ) : (
            <BodyText style={{ fontSize: 11, color: colors.grisOscuro, fontStyle: 'italic' }}>
              Documento interno PetPass × Air Europa
            </BodyText>
          )}
        </View>
      ))}
    </View>
    <BodyText style={{ fontSize: 11, color: colors.grisOscuro, marginTop: 10, lineHeight: 16 }}>
      Última verificación de fuentes: 30 de abril de 2026. Las normativas pueden cambiar; recomendamos comprobar las fuentes antes de cualquier movimiento de mascota.
    </BodyText>
  </Section>
)

const ContactarButton: FC<{ onPress: () => void }> = ({ onPress }) => (
  <TouchableOpacity
    onPress={onPress}
    style={{ backgroundColor: verde, padding: 14, borderRadius: 10, alignItems: 'center', marginTop: 8 }}
  >
    <BodyText style={{ color: colors.blanco, fontWeight: '700', fontSize: 14 }}>
      Contactar con PetPass para gestión documental
    </BodyText>
  </TouchableOpacity>
)

const ExportModal: FC<{
  d: ITravelExportDestination | null
  sources: Record<string, ITravelDocSource>
  onClose: () => void
  onContact: (info: { pais: string; iso: string; vista: 'export' | 'import' }) => void
}> = ({ d, sources, onClose, onContact }) => {
  if (!d) return null
  const fuentes = buildExportFuentes(d, sources)
  const subtitle = `${d.continente} · Cluster ${d.cluster} · Complejidad ${d.complejidad}${d.soloCargo ? ' · SOLO CARGO' : ''}`
  return (
    <ModalShell visible={d !== null} onClose={onClose} title={`${d.pais} · ${d.iso}`} subtitle={subtitle}>
      {d.soloCargo && (
        <Alert kind="block">
          <BodyText style={{ fontWeight: '700', color: blockFg }}>Atención: SOLO CARGO. </BodyText>
          No es posible transportar la mascota en cabina (PETC) ni en bodega como equipaje (AVIH). Debe enviarse como flete aéreo.
        </Alert>
      )}

      <Section title="Documentación principal">
        <Row label="Documento clave" value={d.docKey} />
        <Row label="Vacunación antirrábica" value={d.vacuna} />
        <Row label="Test serológico" value={d.serologia} />
        <Row label="Tratamiento tenia" value={d.tenia} />
        <Row label="Cuarentena" value={d.cuarentena} />
        <Row label="Antelación / plazo" value={d.antelacion} />
      </Section>

      <Section title="Modos de transporte Air Europa">
        <Row label="PETC (cabina)" value={d.petc ? '✓ Disponible' : '✗ No disponible'} />
        <Row label="AVIH (bodega)" value={d.avih ? '✓ Disponible' : '✗ No disponible'} />
        <Row label="Solo cargo" value={d.soloCargo ? '✓ Sí (envío como flete aéreo)' : '✗ No'} />
      </Section>

      <Section title="Razas restringidas">
        <Row label="Clasificación" value={d.razasClasif} />
        <Row label="Razas afectadas" value={d.razasAfectadas} />
        <Row label="Marco legal" value={d.razasMarco} />
      </Section>

      <Section title="Autoridad sanitaria del destino">
        <Row label="Organismo" value={d.autoridad} />
        <Row
          label="Web oficial"
          value={
            <BodyText
              style={{ fontSize: 15, color: verde, fontWeight: '600' }}
              onPress={() => Platform.OS === 'web' && window.open(d.url, '_blank')}
            >
              {d.url}
            </BodyText>
          }
        />
      </Section>

      <Section title="Salida desde España">
        <BodyText style={{ fontSize: 15, color: colors.grisOscuro, lineHeight: 22 }}>
          Para la exportación desde España, su veterinario debe solicitar el Certificado de Exportación para Mascotas a través de la aplicación CEXGAN del MAPA. Cada país de destino tiene su modelo específico de certificado zoosanitario. PetPass gestiona la documentación end-to-end.
        </BodyText>
      </Section>

      <FuentesList fuentes={fuentes} />

      <ContactarButton onPress={() => onContact({ pais: d.pais, iso: d.iso, vista: 'export' })} />
    </ModalShell>
  )
}

const ImportModal: FC<{
  d: ITravelImportOrigin | null
  scenarios: Record<TravelDocEscenario, ITravelImportScenario> | null
  sources: Record<string, ITravelDocSource>
  pev: ITravelPev
  onClose: () => void
  onContact: (info: { pais: string; iso: string; vista: 'export' | 'import' }) => void
}> = ({ d, scenarios, sources, pev, onClose, onContact }) => {
  if (!d || !scenarios) return null
  const e = scenarios[d.escenario]
  const fuentes = buildImportFuentes(d, sources)
  const subtitle = `${d.continente} · ${e.titulo} · Complejidad ${d.complejidad}`
  return (
    <ModalShell visible={d !== null} onClose={onClose} title={`${d.pais} · ${d.iso}`} subtitle={subtitle}>
      {d.notaEspecial && (
        <Alert kind="block">
          <BodyText style={{ fontWeight: '700', color: blockFg }}>Nota crítica: </BodyText>
          {d.notaEspecial}
        </Alert>
      )}
      {d.asimiladoUE && (
        <Alert kind="ok">
          <BodyText style={{ fontWeight: '700', color: okFg }}>País asimilado UE. </BodyText>
          Se considera movimiento intracomunitario para entrada en España. Tratado como Estado Miembro a efectos de documentación de mascotas.
        </Alert>
      )}
      {d.territorios && (
        <Alert kind="info">
          <BodyText style={{ fontWeight: '700', color: infoFg }}>Territorios incluidos: </BodyText>
          {d.territorios}
        </Alert>
      )}

      <Section title={e.titulo}>
        <BodyText style={{ fontSize: 15, color: colors.grisOscuro, marginBottom: 12 }}>{e.desc}</BodyText>
        {e.requisitos.map((r, i) => (
          <Row key={i} label={r.campo} value={r.valor} />
        ))}
      </Section>

      <Section title="Marco legal aplicable">
        <BodyText style={{ fontSize: 15, color: colors.negro }}>{e.base}</BodyText>
      </Section>

      {d.escenario !== 'A' && (
        <Section title="Puntos de Entrada de Viajeros (PEV) en España">
          <BodyText style={{ fontSize: 14, color: colors.grisOscuro, marginBottom: 6 }}>
            <BodyText style={{ fontWeight: '700' }}>Aeropuertos ({pev.aeropuertos.length}): </BodyText>
            {pev.aeropuertos.join(', ')}.
          </BodyText>
          <BodyText style={{ fontSize: 14, color: colors.grisOscuro, marginBottom: 6 }}>
            <BodyText style={{ fontWeight: '700' }}>Puertos ({pev.puertos.length}): </BodyText>
            {pev.puertos.join(', ')}.
          </BodyText>
          <BodyText style={{ fontSize: 14, color: colors.grisOscuro, marginBottom: 10 }}>
            <BodyText style={{ fontWeight: '700' }}>Fronteras terrestres ({pev.fronteras.length}): </BodyText>
            {pev.fronteras.join(', ')}.
          </BodyText>
          <Alert kind="info">
            Lista oficial actualizada por MAPA (Real Decreto 990/2022).
          </Alert>
        </Section>
      )}

      {d.escenario === 'C' && (
        <Section title="Laboratorios autorizados para serología">
          <BodyText style={{ fontSize: 15, color: colors.grisOscuro, lineHeight: 22 }}>
            El test serológico antirrábico (RNATT) debe realizarse en un laboratorio autorizado por la UE. La lista oficial mantenida por DG SANTE está disponible en food.ec.europa.eu. PetPass coordina el envío de muestra y obtención del resultado certificado.
          </BodyText>
        </Section>
      )}

      <FuentesList fuentes={fuentes} />

      <ContactarButton onPress={() => onContact({ pais: d.pais, iso: d.iso, vista: 'import' })} />
    </ModalShell>
  )
}

// ============================== CONTACT FORM MODAL ==============================

interface ContactFormState {
  nombre: string
  email: string
  telefono: string
  fechaViaje: string
  tipoMascota: TravelDocPetType | ''
  mensaje: string
  gdpr: boolean
}

const initialContactState: ContactFormState = {
  nombre: '', email: '', telefono: '', fechaViaje: '', tipoMascota: '', mensaje: '', gdpr: false,
}

const ContactFormModal: FC<{
  info: { pais: string; iso: string; vista: 'export' | 'import' } | null
  onClose: () => void
}> = ({ info, onClose }) => {
  const [form, setForm] = useState<ContactFormState>(initialContactState)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (info) {
      setForm(initialContactState)
      setSubmitting(false)
      setErrorMsg(null)
      setSuccess(false)
    }
  }, [info])

  if (!info) return null

  const motivo = info.vista === 'export'
    ? `viaje desde España a ${info.pais}`
    : `entrada en España desde ${info.pais}`

  const update = (k: keyof ContactFormState, v: any) => setForm((prev) => ({ ...prev, [k]: v }))

  const validate = (): string | null => {
    if (!form.nombre.trim()) return 'Nombre obligatorio'
    if (!form.email.trim()) return 'Email obligatorio'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Email inválido'
    if (!form.tipoMascota) return 'Selecciona el tipo de mascota'
    if (!form.gdpr) return 'Debes aceptar la política de privacidad'
    return null
  }

  const handleSubmit = async () => {
    const err = validate()
    if (err) { setErrorMsg(err); return }
    setSubmitting(true)
    setErrorMsg(null)
    try {
      await submitTravelDocContactRequest({
        paisIso: info.iso,
        vistaType: info.vista,
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim() || undefined,
        fechaViaje: form.fechaViaje || undefined,
        tipoMascota: form.tipoMascota as TravelDocPetType,
        mensaje: form.mensaje.trim() || undefined,
        gdprAccepted: true,
      })
      setSuccess(true)
    } catch (e: any) {
      console.error('submit contact request error', e)
      setErrorMsg(e?.message || 'Error enviando la consulta')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal visible animationType="slide" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: colors.blanco }}>
        <View style={{ backgroundColor: verde, padding: 20, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Headline2 style={{ color: colors.blanco, fontSize: 20, marginBottom: 4 }}>Contactar con PetPass</Headline2>
            <BodyText style={{ color: colors.blanco, opacity: 0.9, fontSize: 12 }}>Consulta sobre {motivo}</BodyText>
          </View>
          <TouchableOpacity
            onPress={onClose}
            style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.25)', alignItems: 'center', justifyContent: 'center' }}
          >
            <BodyText style={{ color: colors.blanco, fontSize: 20, lineHeight: 20 }}>×</BodyText>
          </TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1, padding: 20 }} contentContainerStyle={{ paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
          {success ? (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <Headline2 style={{ color: verde, marginBottom: 12 }}>¡Consulta enviada!</Headline2>
              <BodyText style={{ color: colors.grisOscuro, textAlign: 'center', fontSize: 14, lineHeight: 20 }}>
                Hemos recibido tu solicitud. Un especialista de PetPass te contactará en menos de 24 horas en {form.email}.
              </BodyText>
              <TouchableOpacity onPress={onClose} style={{ marginTop: 24, backgroundColor: verde, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 10 }}>
                <BodyText style={{ color: colors.blanco, fontWeight: '700' }}>Cerrar</BodyText>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {Platform.OS === 'web' && (
                <View style={{ gap: 14 }}>
                  <FormField label="Nombre completo *">
                    <input type="text" value={form.nombre} onChange={(e) => update('nombre', e.target.value)} style={inputStyle as any} />
                  </FormField>

                  <FormField label="Email *">
                    <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} style={inputStyle as any} />
                  </FormField>

                  <FormField label="Teléfono">
                    <input type="tel" value={form.telefono} onChange={(e) => update('telefono', e.target.value)} style={inputStyle as any} />
                  </FormField>

                  <FormField label="Fecha aproximada del viaje">
                    <input type="date" value={form.fechaViaje} onChange={(e) => update('fechaViaje', e.target.value)} style={inputStyle as any} />
                  </FormField>

                  <FormField label="Tipo de mascota *">
                    <select value={form.tipoMascota} onChange={(e) => update('tipoMascota', e.target.value)} style={inputStyle as any}>
                      <option value="">Selecciona…</option>
                      <option value="perro">Perro</option>
                      <option value="gato">Gato</option>
                      <option value="huron">Hurón</option>
                      <option value="otro">Otro</option>
                    </select>
                  </FormField>

                  <FormField label="Mensaje">
                    <textarea
                      value={form.mensaje}
                      onChange={(e) => update('mensaje', e.target.value)}
                      placeholder={`Cuéntanos sobre tu ${motivo}…`}
                      rows={4}
                      style={{ ...(inputStyle as any), resize: 'vertical', minHeight: 90 }}
                    />
                  </FormField>

                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
                    <input
                      type="checkbox"
                      checked={form.gdpr}
                      onChange={(e) => update('gdpr', e.target.checked)}
                      style={{ marginTop: 4 }}
                    />
                    <BodyText style={{ flex: 1, fontSize: 12, color: colors.grisOscuro, lineHeight: 17 }}>
                      He leído y acepto la{' '}
                      <BodyText
                        style={{ color: verde, fontWeight: '600', textDecorationLine: 'underline' }}
                        onPress={() => window.open('https://petpass.pro/politicas-de-privacidad', '_blank')}
                      >
                        política de privacidad
                      </BodyText>
                      {' '}y el tratamiento de mis datos por PetPass conforme al RGPD.
                    </BodyText>
                  </View>
                </View>
              )}

              {errorMsg && (
                <View style={{ marginTop: 14, backgroundColor: blockBg, borderLeftWidth: 4, borderLeftColor: blockFg, padding: 12, borderRadius: 6 }}>
                  <BodyText style={{ color: blockFg, fontSize: 13 }}>{errorMsg}</BodyText>
                </View>
              )}

              <TouchableOpacity
                disabled={submitting}
                onPress={handleSubmit}
                style={{ backgroundColor: submitting ? colors.grisMedio : verde, padding: 14, borderRadius: 10, alignItems: 'center', marginTop: 20 }}
              >
                {submitting ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <ActivityIndicator color={colors.blanco} />
                    <BodyText style={{ color: colors.blanco, fontWeight: '700' }}>Enviando…</BodyText>
                  </View>
                ) : (
                  <BodyText style={{ color: colors.blanco, fontWeight: '700', fontSize: 14 }}>Enviar consulta a PetPass</BodyText>
                )}
              </TouchableOpacity>
            </>
          )}
        </ScrollView>
      </View>
    </Modal>
  )
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  border: `1.5px solid ${grisBorde}`,
  borderRadius: 8,
  outline: 'none',
  background: colors.blanco,
  color: colors.negro,
  boxSizing: 'border-box',
}

const FormField: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <View>
    <BodyText style={{ fontSize: 12, fontWeight: '700', color: colors.negro, marginBottom: 5 }}>{label}</BodyText>
    {children}
  </View>
)
